"use client";
import Link from 'next/link';
import { usePathname } from "next/navigation";
import menu_data from "../../../data/MenuData";
import { useLanguage } from "@/utils/LanguageContext";
import { useState, useEffect } from "react";

const NavMenu = () => {
  const currentRoute = usePathname();
  const { t, lang } = useLanguage();
  const [mounted, setMounted] = useState(false);
  const [activeHash, setActiveHash] = useState("");
  const homePath = `/${lang}/home`;

  useEffect(() => {
    setMounted(true);

    const syncHash = () => setActiveHash(window.location.hash || "");
    syncHash();
    window.addEventListener("hashchange", syncHash);

    return () => window.removeEventListener("hashchange", syncHash);
  }, [currentRoute]);

  const buildLink = (link: string) => {
    if (link.startsWith('http')) return link;
    if (link === '/') return `/${lang}/home`;
    if (link.startsWith('/#')) return `/${lang}/home${link.substring(1)}`;
    return `/${lang}${link}`;
  };
  const shouldOpenInNewTab = (link: string) => link.startsWith("/flipbook/");

  const isHomeSectionLink = (link: string) => link.startsWith("/#");
  const extractHash = (link: string) => (isHomeSectionLink(link) ? link.slice(1) : "");

  const isMenuItemActive = (menuLink: string) => {
    if (isHomeSectionLink(menuLink)) {
      return currentRoute === homePath && activeHash === extractHash(menuLink);
    }

    if (menuLink === "/") {
      return currentRoute === homePath && activeHash === "";
    }

    return currentRoute === buildLink(menuLink);
  };

  const isSubMenuItemActive = (subMenuLink: string) => {
    if (isHomeSectionLink(subMenuLink)) {
      return currentRoute === homePath && activeHash === extractHash(subMenuLink);
    }

    return currentRoute === buildLink(subMenuLink);
  };

  if (!mounted) return null;

  return (
    <>
      <ul
        className={`navigation ${lang === "ar" || lang === "ur" ? "rtl" : "ltr"}`}
        dir={lang === "ar" || lang === "ur" ? "rtl" : "ltr"}
      >
        {menu_data
          .filter((items) => items.page === "nav_1")
          .map((menu: any) => {
            const active =
              isMenuItemActive(menu.link) ||
              (menu.sub_menus &&
                menu.sub_menus.some((sub_m: any) => sub_m.link && isSubMenuItemActive(sub_m.link)));

            return (
              <li key={menu.id} className={menu.has_dropdown ? "menu-item-has-children" : ""}>
                <Link
                  href={buildLink(menu.link)}
                  prefetch={false}
                  target={shouldOpenInNewTab(menu.link) ? "_blank" : undefined}
                  rel={shouldOpenInNewTab(menu.link) ? "noopener noreferrer" : undefined}
                  className={`section-link ${active ? "active" : ""}`}
                  aria-current={active ? "page" : undefined}
                >
                  {t(menu.title)}
                </Link>

                {menu.has_dropdown && menu.sub_menus && (
                  <ul className="sub-menu">
                    {menu.sub_menus.map((sub_m: any, i: number) => (
                      <li key={i}>
                        <Link
                          href={buildLink(sub_m.link)}
                          prefetch={false}
                          target={shouldOpenInNewTab(sub_m.link) ? "_blank" : undefined}
                          rel={shouldOpenInNewTab(sub_m.link) ? "noopener noreferrer" : undefined}
                          className={sub_m.link && isSubMenuItemActive(sub_m.link) ? "active" : ""}
                          aria-current={sub_m.link && isSubMenuItemActive(sub_m.link) ? "page" : undefined}
                        >
                          {t(sub_m.title)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
      </ul>

      <style jsx>{`
        .navigation {
          display: flex;
          justify-content: center; /* ✅ Keep nav items centered */
          align-items: center;
          list-style: none;
          padding: 0;
          margin: 0;

          /* Increase spacing between nav items by 50% */
          gap: 2.25rem;
        }

        .navigation > li {
          margin: 0 !important; /* override theme spacing */
        }

        .menu-item-has-children {
          position: relative;
        }

        .menu-item-has-children::after {
          content: "";
          position: absolute;
          top: 100%;
          left: 0;
          width: 100%;
          height: 18px;
        }

        .navigation.rtl {
          direction: rtl;
        }

        .navigation.ltr {
          direction: ltr;
        }

        :global(.section-link) {
          text-decoration: none;
          color: var(--tg-white);
          font-weight: 500;
          font-size: 1rem;
          white-space: nowrap;
        }

        :global(.section-link.active) {
          color: var(--tg-white);
        }

        :global(.section-link:focus-visible),
        .sub-menu a:focus-visible {
          outline: 2px solid rgba(131, 206, 255, 0.92);
          outline-offset: 4px;
          border-radius: 8px;
        }

        .menu-item-has-children > .sub-menu {
          opacity: 0;
          visibility: hidden;
          pointer-events: none;
          transition: opacity 0.18s ease, visibility 0.18s ease;
        }

        .menu-item-has-children:hover > .sub-menu,
        .menu-item-has-children:focus-within > .sub-menu {
          opacity: 1;
          visibility: visible;
          pointer-events: auto;
        }

        .sub-menu {
          width: fit-content;
          min-width: auto;
          position: absolute;
          top: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
          padding: 8px 20px;
          background: var(--glass-bg);
          backdrop-filter: blur(var(--glass-blur));
          -webkit-backdrop-filter: blur(var(--glass-blur));
          border: 1px solid var(--glass-border);
          border-radius: 12px;
          box-shadow: var(--shadow);
          color: var(--tg-button-text-color);
          z-index: 10;
        }

        .sub-menu li {
          margin: 0;
        }

        .sub-menu a {
          display: block;
          padding: 6px 10px;
          font-size: 0.875rem;
          color: var(--tg-button-text-color);
          cursor: pointer;
          transition: color .2s ease, background .25s ease, transform .05s ease;
          border-radius: 8px;
          border: 1px solid transparent;
        }

        .sub-menu a:hover,
        .sub-menu a.active {
          color: var(--tg-button-text-color);
          background: var(--glass-bg-strong);
          border-color: var(--glass-border-strong);
          box-shadow: 0 8px 24px rgba(100,140,255,.18);
          transform: translateY(-1px);
        }

        .sub-menu:before {
          content: "";
          position: absolute;
          left: 50%;
          top: -7px;
          width: 14px;
          height: 14px;
          margin-left: -7px;
          background: var(--glass-bg);
          border-left: 1px solid var(--glass-border);
          border-top: 1px solid var(--glass-border);
          transform: rotate(45deg);
          z-index: -1;
          backdrop-filter: blur(var(--glass-blur));
          -webkit-backdrop-filter: blur(var(--glass-blur));
        }
      `}</style>
    </>
  );
};

export default NavMenu;
